import castArray from 'lodash/castArray';
import isEmpty from 'lodash/isEmpty';

import {
    BioCodeCreationParams,
    LaboratoryServiceCreateParams,
    LaboratoryServiceListQuery,
    LaboratoryServiceUpdateParams,
} from 'chaika-frontend-types/frontend-cms';

import { getUniqNewIds } from '@utils/getUniqNewIds';
import { removeExtraSpaces } from '@utils/removeExtraSpaces';

import { store } from '@store';
import { serviceService } from '@services/services/serviceService';
import { LaboratoryServiceApi } from '@api/LaboratoryServiceApi';
import { LaboratoryApi } from '@api/LaboratoryApi';
import { BioCodeApi } from '@api/BioCodeApi';
import { ValidationParams } from '@components/types';
import { LaboratoryServiceServiceStatus } from 'chaika-enums';
import { translate } from '@chaika/i18n/helper';

class LaboratoryService {
    private isLoaded = false;
    private isDataLoaded = false;

    public async load(
        params: LaboratoryServiceListQuery = { status: LaboratoryServiceServiceStatus.Active },
    ): Promise<void> {
        if (!this.isLoaded) {
            await this.reload(params);
        }
    }

    public async reload(
        params: LaboratoryServiceListQuery = { status: LaboratoryServiceServiceStatus.Active },
    ): Promise<void> {
        const [{ laboratoryServices }] = await Promise.all([LaboratoryServiceApi.getList(params), this.loadData()]);

        store.laboratoryStore.laboratoryServiceList.set(laboratoryServices);
        this.isLoaded = true;
    }

    public async loadByIds(id: string | string[]): Promise<void> {
        const { laboratoryServiceList } = store.laboratoryStore;
        const uniqIds = getUniqNewIds(laboratoryServiceList.ids, castArray(id));

        if (!isEmpty(uniqIds)) {
            const [{ laboratoryServices }] = await Promise.all([
                LaboratoryServiceApi.getList({ ids: uniqIds.join(',') }),
                this.loadData(),
            ]);
            laboratoryServiceList.addIfNotExist(laboratoryServices);
        }
    }

    public async loadData(): Promise<void> {
        if (!this.isDataLoaded) {
            const { bioCodeList, laboratoryList } = store.laboratoryStore;
            const [bioCodes, laboratories] = await Promise.all([BioCodeApi.getList(), LaboratoryApi.getList()]);

            bioCodeList.set(bioCodes);
            laboratoryList.set(laboratories);

            this.isDataLoaded = true;
        }
    }

    public async loadBioCodes(): Promise<void> {
        if (!this.isDataLoaded) {
            const { bioCodeList } = store.laboratoryStore;

            const bioCodes = await BioCodeApi.getList();

            bioCodeList.set(bioCodes);

            this.isDataLoaded = true;
        }
    }

    public validateCreateParams({
        serviceId,
        laboratoryId,
        labCode,
    }: Partial<LaboratoryServiceCreateParams>): ValidationParams {
        return {
            isValid: !!serviceId && !!laboratoryId && !!labCode,
            message: translate('labReqParamsText'),
        };
    }

    public async create(params: LaboratoryServiceCreateParams): Promise<void> {
        await Promise.all([LaboratoryServiceApi.create(params), serviceService.loadByIds([params.serviceId])]);
        const now = new Date().toISOString();

        store.laboratoryStore.laboratoryServiceList.add([
            { ...params, createdAt: now, updatedAt: now, status: LaboratoryServiceServiceStatus.Active },
        ]);
    }

    public async update(id: string, updateParams: LaboratoryServiceUpdateParams): Promise<void> {
        const { laboratoryServiceList } = store.laboratoryStore;

        await LaboratoryServiceApi.update(id, updateParams);

        laboratoryServiceList.update(id, updateParams);
    }

    public async createBioCode(params: BioCodeCreationParams): Promise<void> {
        const { bioCodeList } = store.laboratoryStore;
        const formattedName = removeExtraSpaces(params.name).split(' ').join('.');

        await BioCodeApi.create(params);
        bioCodeList.add([{ ...params, name: formattedName }]);
    }

    public async toggleStatus(id: string): Promise<void> {
        const { laboratoryServiceList } = store.laboratoryStore;
        const current = laboratoryServiceList.getExisting(id);
        const status =
            current.status === LaboratoryServiceServiceStatus.Active
                ? LaboratoryServiceServiceStatus.Archived
                : LaboratoryServiceServiceStatus.Active;
        await LaboratoryServiceApi.updateStatus(id, status);

        laboratoryServiceList.update(id, { status });
    }

    public async loadServices(): Promise<void> {
        await serviceService.loadByIds(store.laboratoryStore.laboratoryServiceList.laboratoryServiceIds);
    }
}

export const laboratoryService = new LaboratoryService();
